// OAuth callback page. The provider redirects here with `?code=...`
// after the user signs in; we POST that code at the existing api
// callback endpoint to exchange it for an Agami access_token, persist
// the session, and land the user on their requested page.

import { useEffect, useState } from "react";
import { persistSession, type AuthCallbackPayload } from "./session";
import { POST_LOGIN_KEY } from "./Login";
import { authBase } from "./oauth-base";

interface Props {
  provider: "google" | "microsoft";
}

export function AuthCallback({ provider }: Props) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void exchange(provider).catch((e) => {
      console.error("Auth callback failed:", e);
      setError(e instanceof Error ? e.message : String(e));
      window.setTimeout(() => {
        window.location.replace(`/login?error=callback_failed`);
      }, 2500);
    });
  }, [provider]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center text-sm text-red-700">
          <div className="font-medium">Couldn't finish signing you in.</div>
          <div className="mt-1 text-red-600/80">{error}</div>
          <div className="mt-3 text-neutral-500">Redirecting back to login…</div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="flex flex-col items-center gap-3 text-neutral-600">
        <Spinner />
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  );
}

async function exchange(provider: "google" | "microsoft"): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  if (!code) throw new Error("Missing authorization code.");
  const state = params.get("state");
  const qs = new URLSearchParams({ code });
  if (state) qs.set("state", state);

  // credentials: 'include' carries the CSRF cookie set when the user
  // clicked Sign in; the api's state-binding check needs it.
  const resp = await fetch(`${authBase()}/auth/${provider}/callback?${qs}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  // The state-validation failure path 302s back to /login. fetch
  // follows it; if we end up on a non-JSON page, bounce to login.
  if (resp.redirected && resp.url.includes("/login")) {
    window.location.replace("/login?error=oauth_state");
    return;
  }
  if (!resp.ok) {
    let detail = "Sign-in failed.";
    try {
      const j = await resp.json();
      if (j?.detail) detail = String(j.detail);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }

  const data = (await resp.json()) as AuthCallbackPayload;
  if (!data.access_token) throw new Error("Server returned no access token.");

  // MCP OAuth flow: the backend tucked the original Claude.ai
  // authorize request into `oauth_continuation`. Stash the JWT +
  // continuation in sessionStorage and bounce to /authorize, which
  // POSTs them to /oauth/authorize/complete to mint the OAuth code
  // and redirect to Claude.ai. We do NOT persistSession() here —
  // the user signed in just to complete the consent grant, not to
  // use the app.
  if (data.oauth_continuation) {
    sessionStorage.setItem("agami_oauth_jwt", data.access_token);
    sessionStorage.setItem(
      "agami_oauth_request",
      JSON.stringify(data.oauth_continuation),
    );
    window.location.replace("/authorize");
    return;
  }

  persistSession(data);

  const next = sessionStorage.getItem(POST_LOGIN_KEY) || "/";
  sessionStorage.removeItem(POST_LOGIN_KEY);
  window.location.replace(next);
}

function Spinner() {
  return (
    <svg className="animate-spin h-5 w-5 text-brand" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
